import { useCleanPathDefault } from '@/hooks/useCleanPathDefault'
import { Syllabus } from '@/kontent/content-types'
import CoursePerformance from '@/legacy-ported/components/syllabus/CoursePerformance'
import { CommonContentTab } from '@/pages/learning-areas/[learningarea]/[syllabus]/[tab]'
import { CommonPageProps, Mapping, TaxoStageWithLifeSkill } from '@/types'
import { byTaxoCodename, getLinkedItems } from '@/utils'
import { IContentItemsContainer, Responses } from '@kontent-ai/delivery-sdk'
import Head from 'next/head'
import { NextRouter, useRouter } from 'next/router'
import { SyllabusAssessment } from '../assessment/SyllabusAssessment'
import { GridCol } from '../nsw/grid/GridCol'
import { GridWrapper } from '../nsw/grid/GridWrapper'
import { SideNav, SideNavItem } from '../nsw/side-nav/SideNav'
import { CommonCopyUrlWrapper } from '../ui/copy-to-clipboard/CommonCopyUrlWrapper'
import { SyllabusTabsTitle } from '../ui/syllabus-tabs/SyllabusTabsTitle'
import { overviewNavStyle } from './Overview'

export const AssessmentContentV2 = ({
	syllabus,
	linkedItems,
	mappings,
	currentStages,
	syllabusResponse,
	lazy = true,
}: {
	syllabus: Syllabus
	linkedItems: IContentItemsContainer
	mappings: Mapping[]
	currentStages: TaxoStageWithLifeSkill[]
	syllabusResponse?: Responses.IViewContentItemResponse<Syllabus>
	lazy?: boolean
}) => {
	const router = useRouter()
	const { route, query } = router
	const currentUrl = useCleanPathDefault()
	const {
		learningarea: paramLearningArea,
		syllabus: paramSyllabus,
		section,
	} = (router?.['components']?.[route] as NextRouter)?.query || query

	const assessmentPages = getLinkedItems(
		syllabus.elements.assessments_pages,
		syllabusResponse.linkedItems,
	)

	if (!section) {
		// Redirect to the first assessment page
		router.replace(
			`/learning-areas/${paramLearningArea}/${paramSyllabus}/assessment/${assessmentPages?.[0]?.elements?.slug?.value}`,
		)
	}

	const staticSideNavItems: SideNavItem[] = assessmentPages.map((item) => {
		return {
			text: item.elements.title.value,
			href: `/learning-areas/${paramLearningArea}/${paramSyllabus}/assessment/${item.elements.slug.value}`,
			isActive: item.elements.slug.value === section,
			onClick: null,
			scroll: false,
		}
	})

	const currentSectionTitle = staticSideNavItems?.find(
		(item) => item.isActive,
	)?.text

	const assessmentPage = assessmentPages.find(
		(item) => item.elements.slug.value === section,
	)

	const assessmentStageContents = getLinkedItems(
		assessmentPage?.elements.assessments,
		syllabusResponse.linkedItems,
	)

	return (
		<>
			<Head>
				<meta
					name="description"
					content={assessmentPage?.elements.seo_description.value}
				/>
			</Head>
			<GridWrapper className="mt-0">
				<GridCol lg={3}>
					<SideNav
						css={overviewNavStyle}
						className="block"
						items={staticSideNavItems}
					/>
				</GridCol>
				<GridCol lg={9}>
					<CommonCopyUrlWrapper url={currentUrl} className="mb-8">
						<h3>{currentSectionTitle}</h3>
					</CommonCopyUrlWrapper>
					<CoursePerformance
						info={assessmentPage?.elements.info}
						sections={assessmentStageContents}
						mappings={mappings}
						linkedItems={syllabusResponse.linkedItems}
						suffixForHeading={syllabus.system.codename}
						isUseNonFullWidthWrapper={false}
						sectionTitleTag={'h4'}
						assessmentGroupTitleTag={'h5'}
						contentAfterSections={assessmentPage?.elements.content}
					/>
				</GridCol>
			</GridWrapper>
		</>
	)
}

export const Assessment = ({
	mappings,
	data,
}: CommonPageProps<Syllabus, CommonContentTab<Syllabus>>) => {
	const { syllabus: syllabusResponse } = data
	const syllabus = syllabusResponse.item
	const currentStages =
		syllabus.elements.stages__stages.value.map(byTaxoCodename)
	const isAssessmentModelV2 =
		syllabus.elements.assessment_model?.value?.[0]?.codename === 'v2'

	return (
		<div className="px-4 pt-8">
			<SyllabusTabsTitle
				tabId="assessment"
				mappings={mappings}
				syllabusCodename={syllabus.system.codename}
				className={isAssessmentModelV2 ? '!mb-0' : ''}
			/>

			{isAssessmentModelV2 ? (
				<AssessmentContentV2
					syllabus={syllabus}
					linkedItems={syllabusResponse.linkedItems}
					mappings={mappings}
					currentStages={currentStages}
					syllabusResponse={syllabusResponse}
					lazy={false}
				/>
			) : (
				<SyllabusAssessment
					syllabus={syllabus}
					mappings={mappings}
					linkedItems={syllabusResponse.linkedItems}
					currentStages={currentStages}
					syllabusResponse={syllabusResponse}
					lazy={false}
				/>
			)}
		</div>
	)
}

export default Assessment
